import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import './ClientHeader.scss';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import ClientInstructions from '../ClientInstructions/ClientInstructions';
import useAppContext from '../../hooks/useAppContext/useAppContext';
import ClientInstructionsVr from '../ClientInstructionsVr/ClientInstructionsVr';
import { hashCurrentSession } from '../../utils/sessionUUIDToCode';
import Key from '../../assets/mainControls/IconKey.svg?react';
import useClientMediaState from '../../hooks/useClientMediaState/useClientMediaState';

interface ClientHeaderProps {
  clientInstructionsOpened: boolean;
  setClientInstructionsOpened: (value: boolean) => void;
}

const ClientHeader: React.FC<ClientHeaderProps> = ({ clientInstructionsOpened, setClientInstructionsOpened }) => {
  const { t } = useTranslation();
  const { hasDevice, sessionId, treatmentId } = useAppContext();
  const hash = hashCurrentSession(sessionId, treatmentId, 'client-hash');
  const { clientMediaURI, isClientPlayerVisible } = useClientMediaState();
  const clientInstructions = useMemo(() => {
    if (!clientInstructionsOpened) {
      return <></>;
    }

    if (hasDevice) {
      return <ClientInstructionsVr setOpen={setClientInstructionsOpened} />;
    }

    return <ClientInstructions setOpen={setClientInstructionsOpened} />;
  }, [clientInstructionsOpened, setClientInstructionsOpened, hasDevice]);

  return (
    <div className="client-header">
      <div className="title-container">
        <img src={'assets/logo-no-bg-light.png'} alt="psylaris | emdr-remote" />{' '}
      </div>
      <div className="header-right">
        <Tooltip title={t('clientSessionID').toString()} placement="left" PopperProps={{ disablePortal: true }}>
          <div className="client-hash-container">
            <Key className="client-key-icon" />
            {hash}
          </div>
        </Tooltip>
        <LanguageSelector isMedia={!!clientMediaURI && isClientPlayerVisible} size={'large'} />
        <Tooltip title={t('info').toString()} placement="bottom-start" PopperProps={{ disablePortal: true }}>
          <span>
            <button
              className="info-button"
              onClick={e => {
                setClientInstructionsOpened(!clientInstructionsOpened);
                e.currentTarget.blur(); // Remove focus from the button after click
              }}
            >
              <svg width="88" height="88" viewBox="0 0 88 88" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M44 88C68.3005 88 88 68.3005 88 44C88 19.6995 68.3005 0 44 0C19.6995 0 0 19.6995 0 44C0 68.3005 19.6995 88 44 88ZM44 80C63.8823 80 80 63.8823 80 44C80 24.1177 63.8823 8 44 8C24.1177 8 8 24.1177 8 44C8 63.8823 24.1177 80 44 80ZM51 25.0133C51 26.6705 50.4306 28.0851 49.2865 29.2481C48.1451 30.417 46.7689 31 45.1606 31C43.5469 31 42.168 30.417 41.0131 29.2481C39.8609 28.0851 39.2834 26.6705 39.2834 25.0133C39.2834 23.3591 39.8609 21.9416 41.0131 20.7637C42.1653 19.5859 43.5469 19 45.1606 19C46.7689 19 48.1451 19.5889 49.2865 20.7637C50.4306 21.9416 51 23.3591 51 25.0133ZM50.4274 64.4664C48.7098 65.2918 47.3361 65.9195 46.3135 66.3523C45.2909 66.785 44.101 67 42.7462 67C40.6657 67 39.047 66.3809 37.8924 65.1485C36.7379 63.9132 36.1606 62.3455 36.1606 60.4453C36.1606 59.7087 36.2031 58.9521 36.2902 58.1811C36.3774 57.4101 36.5164 56.5388 36.7073 55.5672L38.8561 46.3213C39.047 45.4356 39.2096 44.5959 39.3392 43.802C39.4711 43.0138 39.5347 42.2887 39.5347 41.6352C39.5347 40.4544 39.3344 39.629 38.9339 39.1647C38.5333 38.7004 37.7699 38.4653 36.6366 38.4653C36.0805 38.4653 35.5103 38.5743 34.9283 38.7835C34.344 38.9927 34 36.5336 34 36.5336C35.4067 35.8372 36.752 35.241 38.0385 34.7452C39.325 34.2493 40.5408 34 41.6906 34C43.757 34 45.3522 34.6076 46.4714 35.8228C47.5906 37.038 48.1514 38.6144 48.1514 40.5576C48.1514 40.9588 48.1137 41.6668 48.0359 42.6785C47.9581 43.6931 47.8144 44.6217 47.6047 45.4672L45.4653 54.6787C45.2909 55.4182 45.1331 56.2637 44.9964 57.2152C44.855 58.161 44.7867 58.8833 44.7867 59.3676C44.7867 60.5915 45.0105 61.4255 45.4606 61.8697C45.913 62.314 46.6929 62.5347 47.8003 62.5347C48.321 62.5347 48.9124 62.4229 49.5698 62.2022C50.2272 61.9815 50.4274 64.4664 50.4274 64.4664Z"
                />
              </svg>
            </button>
          </span>
        </Tooltip>
      </div>
      {clientInstructions}
    </div>
  );
};

export default ClientHeader;
